import React, { useState, useEffect } from "react";

// Charts render into a ~500px-wide box on desktop, but on phones the SVG is
// scaled down to ~300px, shrinking labels below legibility. Render a smaller
// coordinate system on mobile instead so text stays readable.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = e => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return isMobile;
}

function LineChart({ data, pct, isRank }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const isMobile = useIsMobile();
  if (!data) return null;

  const years = Object.keys(data).map(Number).sort((a, b) => a - b);
  const counts = years.map(y => data[y]);
  const max = isRank ? Math.max(1000, Math.max(...counts)) : Math.max(...counts);
  const min = isRank ? 1 : 0;
  const width = isMobile ? 340 : 500;
  const height = isMobile ? 250 : 280;
  const margin = isMobile
    ? { top: 16, right: 14, bottom: 52, left: 56 }
    : { top: 20, right: 30, bottom: 60, left: 70 };

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const points = years.map((year, i) => {
    const x = margin.left + (i / (years.length - 1)) * chartWidth;
    const normalized = isRank ? (max - data[year]) / (max - min) : (data[year] - min) / (max - min);
    const y = margin.top + chartHeight - normalized * chartHeight;
    return `${x},${y}`;
  }).join(' ');
  
  const tickCount = Math.min(isMobile ? 5 : 9, years.length);
  const yearStep = Math.max(1, Math.round(years.length / (tickCount - 1)));

  const xTicks = years.filter((i, _) => ((i+(yearStep - (years[years.length-1]+yearStep))) % yearStep === 0));
  const yTicks = isRank
    ? [min, Math.round(min + (max - min) * 0.25), Math.round(min + (max - min) * 0.5), Math.round(min + (max - min) * 0.75), max]
    : [0, max * 0.25, max * 0.5, max * 0.75, max];

  const hoverAreas = years.map((year, i) => {
    const x = margin.left + (i / (years.length - 1)) * chartWidth;
    const normalized = isRank ? (max - data[year]) / (max - min) : (data[year] - min) / (max - min);
    const y = margin.top + chartHeight - normalized * chartHeight;
    return (
      <circle
        key={`hover-${i}`}
        cx={x}
        cy={y}
        r={isMobile ? 13 : 8}
        fill="transparent"
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setHoveredPoint({ year, value: data[year], x, y })}
        onMouseLeave={() => setHoveredPoint(null)}
      />
    );
  });

  return (
    <div className="chart-container" style={{ position: 'relative' }}>
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        style={{ maxWidth: '100%', maxHeight: height }}
      >
        <rect
          x={margin.left}
          y={margin.top}
          width={chartWidth}
          height={chartHeight}
          fill="#faf7f0"
          stroke="none"
        />
        {yTicks.map((v, i) => {
          const normalized = isRank ? (max - v) / (max - min) : (v - min) / (max - min);
          const y = margin.top + chartHeight - normalized * chartHeight;
          return (
            <line
              key={`grid-y-${i}`}
              x1={margin.left}
              y1={y}
              x2={margin.left + chartWidth}
              y2={y}
              stroke="#e2e8f0"
              strokeWidth={1}
              strokeDasharray={v === (isRank ? max : 0) ? 'none' : '3,3'}
            />
          );
        })}
        <line
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left + chartWidth}
          y2={margin.top + chartHeight}
          stroke="#4a5568"
          strokeWidth={2}
        />
        <line
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left}
          y2={margin.top}
          stroke="#4a5568"
          strokeWidth={2}
        />
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f4a6a6" />
            <stop offset="100%" stopColor="#f4a6a6" />
          </linearGradient>
        </defs>
        <polyline
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        {hoverAreas}
        {hoveredPoint && (
          <circle
            cx={hoveredPoint.x}
            cy={hoveredPoint.y}
            r={4}
            fill="#f4a6a6"
            stroke="#ffffff"
            strokeWidth={2}
            style={{ pointerEvents: 'none' }}
          />
        )}
        {xTicks.map((yr, i) => {
          const x = margin.left + (years.indexOf(yr) / (years.length - 1)) * chartWidth;
          return (
            <text
              key={`x-label-${i}`}
              x={x}
              y={height - 35}
              textAnchor="middle"
              fontSize={12}
              fill="#718096"
              fontFamily="Inter, sans-serif"
            >
              {yr}
            </text>
          );
        })}
        {yTicks.map((v, i) => {
          const normalized = isRank ? (max - v) / (max - min) : (v - min) / (max - min);
          const y = margin.top + chartHeight - normalized * chartHeight;
          const label = isRank ? Math.round(v) : pct ? v.toFixed(2) : v.toFixed(0);
          return (
            <text
              key={`y-label-${i}`}
              x={margin.left - 10}
              y={y + 4}
              textAnchor="end"
              fontSize={isMobile ? 11 : 12}
              fill="#718096"
              fontFamily="Inter, sans-serif"
            >
              {label}
            </text>
          );
        })}
        <text
          x={margin.left + chartWidth / 2}
          y={height - 5}
          textAnchor="middle"
          fontSize={isMobile ? 12 : 14}
          fill="#4a5568"
          fontWeight="500"
          fontFamily="Inter, sans-serif"
        >
          Year
        </text>
        <text
          x={isMobile ? 13 : 15}
          y={margin.top + chartHeight / 2}
          transform={`rotate(-90 ${isMobile ? 13 : 15} ${margin.top + chartHeight / 2})`}
          textAnchor="middle"
          fontSize={isMobile ? 12 : 14}
          fill="#4a5568"
          fontWeight="500"
          fontFamily="Inter, sans-serif"
        >
          {isRank ? 'Rank' : pct ? '% of births' : 'Count'}
        </text>
      </svg>
      {hoveredPoint && (
        <div
          className="chart-tooltip"
          style={{
            position: 'absolute',
            left: `${(hoveredPoint.x / width) * 100}%`,
            top: `${(hoveredPoint.y / height) * 100}%`,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none'
          }}
        >
          <div className="tooltip-content">
            <div className="tooltip-year">{hoveredPoint.year}</div>
            <div className="tooltip-value">
              {isRank ? `Rank #${hoveredPoint.value}` : pct ? `${hoveredPoint.value.toFixed(2)}%` : hoveredPoint.value.toLocaleString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BarChart({ data }) {
  const isMobile = useIsMobile();
  if (!data) return null;
  const labels = Object.keys(data);
  const values = labels.map(k => data[k] || 0);
  const max = 100;
  const width = isMobile ? 340 : 500;
  const height = isMobile ? 250 : 280;
  const margin = isMobile
    ? { top: 16, right: 10, bottom: 46, left: 48 }
    : { top: 20, right: 30, bottom: 50, left: 70 };

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  const barWidth = (chartWidth / labels.length) * 0.8;
  const barSpacing = (chartWidth / labels.length) * 0.2;
  const colors = ['#a8c09a', '#f4a6a6', '#e6c068', '#9bb5d6', '#d4a5a5', '#b8d4b8'];

  const bars = labels.map((k, i) => {
    const barHeight = (values[i] / max) * chartHeight;
    const x = margin.left + i * (barWidth + barSpacing) + barSpacing / 2;
    const y = margin.top + chartHeight - barHeight;
    return (
      <g key={k}>
        <rect
          x={x}
          y={y}
          width={barWidth}
          height={barHeight}
          fill={colors[i % colors.length]}
          stroke="#ffffff"
          strokeWidth={1}
          rx={4}
        />
        <text
          x={x + barWidth / 2}
          y={height - 30}
          textAnchor="middle"
          fontSize={isMobile ? 9 : 11}
          fill="#4a5568"
          fontWeight="500"
          fontFamily="Inter, sans-serif"
          style={k === 'AIAN' ? { cursor: 'help' } : undefined}
        >
          {k}
          {k === 'AIAN' && <title>American Indian and Alaskan Native</title>}
        </text>
        <text
          x={x + barWidth / 2}
          y={y - 8}
          textAnchor="middle"
          fontSize={10}
          fill="#718096"
          fontFamily="Inter, sans-serif"
        >
          {values[i].toFixed(1)}
        </text>
      </g>
    );
  });

  const yTicks = [0, 25, 50, 75, 100];

  return (
    <div className="chart-container">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        style={{ maxWidth: '100%', maxHeight: height }}
      >
        <rect
          x={margin.left}
          y={margin.top}
          width={chartWidth}
          height={chartHeight}
          fill="#faf7f0"
          stroke="none"
        />
        {yTicks.map((v, i) => {
          const y = margin.top + chartHeight - (v / 100) * chartHeight;
          return (
            <line
              key={`grid-y-${i}`}
              x1={margin.left}
              y1={y}
              x2={margin.left + chartWidth}
              y2={y}
              stroke="#e2e8f0"
              strokeWidth={1}
              strokeDasharray={v === 0 ? 'none' : '3,3'}
            />
          );
        })}
        <line
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left + chartWidth}
          y2={margin.top + chartHeight}
          stroke="#4a5568"
          strokeWidth={2}
        />
        <line
          x1={margin.left}
          y1={margin.top + chartHeight}
          x2={margin.left}
          y2={margin.top}
          stroke="#4a5568"
          strokeWidth={2}
        />
        {bars}
        {yTicks.map((v, i) => {
          const y = margin.top + chartHeight - (v / 100) * chartHeight;
          return (
            <text
              key={`y-tick-${i}`}
              x={margin.left - 10}
              y={y + 4}
              textAnchor="end"
              fontSize={isMobile ? 11 : 12}
              fill="#718096"
              fontFamily="Inter, sans-serif"
            >
              {v}
            </text>
          );
        })}
        <text
          x={margin.left + chartWidth / 2}
          y={height - 5}
          textAnchor="middle"
          fontSize={isMobile ? 12 : 14}
          fill="#4a5568"
          fontWeight="500"
          fontFamily="Inter, sans-serif"
        >
          Demographics
        </text>
        <text
          x={isMobile ? 13 : 15}
          y={margin.top + chartHeight / 2}
          transform={`rotate(-90 ${isMobile ? 13 : 15} ${margin.top + chartHeight / 2})`}
          textAnchor="middle"
          fontSize={isMobile ? 12 : 14}
          fill="#4a5568"
          fontWeight="500"
          fontFamily="Inter, sans-serif"
        >
          Percent
        </text>
      </svg>
    </div>
  );
}

export default function NameInfo({ name, meta, gender, showPctToggle = true }) {
  const [chartType, setChartType] = useState('pct');
  const [useCluster, setUseCluster] = useState(false);
  const updateUseCluster = (val) => {
    setUseCluster(val);
    if (chartType === 'rank') {
      setChartType('pct');
    }
  };

  const fmt = n => Number(n).toLocaleString();
  if (!meta) {
    return (
      <div className="name-info">
        <div className="no-data">No name data available for {name}</div>
      </div>
    );
  }

  // Fetch the right info for the selected gender
  const activeMeta = useCluster && meta.cluster ? meta.cluster.meta : meta;
  let years = null;
  let peakYear = null;
  let peakYearBirths = null;
  let recentYear = null;
  let recentCount = null;
  let recentPct = null;
  let recentRank = null;
  let totalBirths = null;

  if (!activeMeta) {
    () => {}; // Do nothing if activeMeta is undefined
  } else if (gender === 'neutral') {
    years = activeMeta?.byYear ? Object.keys(activeMeta.byYear).map(Number) : [];
    peakYear = activeMeta?.peakYear;
    peakYearBirths = activeMeta?.byYear[peakYear];
    recentYear = years.length ? Math.max(...years) : null;
    recentCount = recentYear ? activeMeta.byYear[recentYear] || 0 : null;
    recentPct = recentYear && activeMeta.byYearPct ? activeMeta.byYearPct[recentYear] : null;
    recentRank = recentYear && activeMeta.byYearRank ? activeMeta.byYearRank[recentYear] : null;
    totalBirths = activeMeta.total;
  } else if (gender === 'male') {
    years = activeMeta.byYearSex ? Object.keys(activeMeta.byYearSex).map(Number) : [];
    peakYear = activeMeta?.peakYearM;
    peakYearBirths = activeMeta?.byYearSex?.[peakYear]?.M;
    recentYear = years.length ? Math.max(...years) : null;
    recentCount = recentYear ? activeMeta.byYearSex[recentYear]['M'] || 0 : null;
    recentPct = recentYear && activeMeta.byYearPctM ? activeMeta.byYearPctM[recentYear] : null;
    recentRank = recentYear && activeMeta.byYearRankM ? activeMeta.byYearRankM[recentYear] : null;
    totalBirths = activeMeta?.bySex['M'];
  } else if (gender === 'female') {
    years = activeMeta.byYearSex ? Object.keys(activeMeta.byYearSex).map(Number) : [];
    peakYear = activeMeta?.peakYearF;
    peakYearBirths = activeMeta?.byYearSex?.[peakYear]?.F;
    recentYear = years.length ? Math.max(...years) : null;
    recentCount = recentYear ? activeMeta.byYearSex[recentYear]['F'] || 0 : null;
    recentPct = recentYear && activeMeta.byYearPctF ? activeMeta.byYearPctF[recentYear] : null;
    recentRank = recentYear && activeMeta.byYearRankF ? activeMeta.byYearRankF[recentYear] : null;
    totalBirths = activeMeta?.bySex['F'];
  }
  

  let chartData = null;
  let isRank = false;
  let isPct = false;

  if (activeMeta) {
    if (chartType === 'rank') {
      isRank = true;
      if (gender === 'male' && activeMeta.byYearRankM) {
        chartData = activeMeta.byYearRankM;
      } else if (gender === 'female' && activeMeta.byYearRankF) {
        chartData = activeMeta.byYearRankF;
      } else if (activeMeta.byYearRank) {
        chartData = activeMeta.byYearRank;
      }
    } else if (chartType === 'pct' && activeMeta.byYearPct) {
      chartData = activeMeta.byYearPct;
      isPct = true;
      if (gender === 'male' && activeMeta.byYearPctM) {
        chartData = activeMeta.byYearPctM;
      } else if (gender === 'female' && activeMeta.byYearPctF) {
        chartData = activeMeta.byYearPctF;
      } else if (activeMeta.byYearPct) {
        chartData = activeMeta.byYearPct;
      }
    } else if (chartType === 'count' && activeMeta.byYear) {
      if (gender === 'male' && activeMeta.byYearSex) {
        chartData = Object.fromEntries(Object.entries(activeMeta.byYearSex).map(([year, genderVals]) => [year, genderVals['M']]));
      } else if (gender === 'female' && activeMeta.byYearSex) {
        chartData = Object.fromEntries(Object.entries(activeMeta.byYearSex).map(([year, genderVals]) => [year, genderVals['F']]));
      } else if (activeMeta.byYear) {
        chartData = activeMeta.byYear;
      }
    }
  }

  return (
    <div className="name-info">
      <div className="name-info-header">
        <h3 className="info-title">{name}</h3>
        <a
          href={`https://www.behindthename.com/name/${encodeURIComponent(name)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="behind-name-link"
        >
          📖 Learn more about this name
        </a>
      </div>
      <div className="info-header">
        <ul className="meta-stats">
          <li>
            <strong>Last year:</strong>{' '}
            {`${fmt(recentCount)} births${typeof recentRank === 'number' ? ` (rank #${recentRank})` : ''}${typeof recentPct === 'number' ? ` (1 in ${Math.round(1/(recentPct/100))})` : ''}`}
          </li>
          <li>
            <strong>Total births:</strong> {totalBirths ? fmt(totalBirths) : 'N/A'}
          </li>
          <li>
            <strong>Peak year:</strong> {(peakYear && peakYearBirths) ? `${peakYear} (${fmt(peakYearBirths)})` : 'N/A'}
          </li>
        </ul>
        {meta.cluster && (
          <div className="cluster-section">
            <div className="cluster-names">
              <strong>Similar Names:</strong> {meta.cluster.names.join(', ')}
            </div>
            <div className="cluster-toggle-wrapper">
              <label className="cluster-toggle">
                <input
                  type="checkbox"
                  checked={useCluster}
                  onChange={e => updateUseCluster(e.target.checked)}
                />
                <span className="cluster-toggle-slider" />
                <span className="cluster-toggle-text">Show stats for all similar names</span>
              </label>
            </div>
          </div>
        )}
      </div>
      <div className="charts">
        <div className="chart" key="trend">
          <h3 className="chart-title">Popularity Trend</h3>
          {chartData ? (
            <>
              <LineChart data={chartData} pct={isPct} isRank={isRank} />
              {showPctToggle && (
                <div className="chart-control">
                  <label>
                    View:
                    <select
                      value={chartType}
                      onChange={e => setChartType(e.target.value)}
                    >
                      <option value="pct">% of births</option>
                      <option value="count">Count</option>
                      {!useCluster && <option value="rank">Rank</option>}
                    </select>
                  </label>
                </div>
              )}
            </>
          ) : (
            <div className="no-data">No trend data</div>
          )}
        </div>
        <div className="chart" key="demo">
          <h3 className="chart-title">Births by Demographic</h3>
          {activeMeta && activeMeta.demographics ? (
            <BarChart
              data={{
                White: activeMeta.demographics.pctwhite,
                Hispanic: activeMeta.demographics.pcthispanic,
                Black: activeMeta.demographics.pctblack,
                API: activeMeta.demographics.pctapi,
                AIAN: activeMeta.demographics.pctaian,
                '2+Race': activeMeta.demographics.pct2prace
              }}
            />
          ) : (
            <div className="no-data">No demographic data</div>
          )}
        </div>
      </div>
    </div>
  );
}
